/**
 * Tâche 1152 (Eric 2026-06-17) — Quota d'envoi Resend.
 *
 * « You have reached your daily email sending quota » arrive sans préavis :
 * d'un envoi à l'autre, tout s'arrête. Resend n'expose aucun compteur, donc
 * Knoria compte lui-même. Ce panneau n'apparaît qu'à partir de 75 % — en
 * dessous, il n'aurait rien à dire — et les limites y sont modifiables, parce
 * qu'un plan Resend payant rendrait l'alerte fausse.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gauge } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const TONE = {
  warning: {
    box: "border-amber-300 bg-amber-50",
    text: "text-amber-900",
    bar: "bg-amber-500",
    icon: "text-amber-600",
  },
  critical: {
    box: "border-orange-300 bg-orange-50",
    text: "text-orange-900",
    bar: "bg-orange-500",
    icon: "text-orange-600",
  },
  exhausted: {
    box: "border-red-300 bg-red-50",
    text: "text-red-900",
    bar: "bg-red-600",
    icon: "text-red-600",
  },
};

const Bar = ({ label, counter, tone, testid }) => (
  <div className="mt-2" data-testid={testid}>
    <div className="flex items-baseline justify-between text-xs">
      <span>{label}</span>
      <span className="font-semibold">
        {counter.sent} / {counter.limit} ({counter.percent} %)
      </span>
    </div>
    <div className="mt-1 h-2 w-full rounded-full bg-white/70 overflow-hidden">
      <div
        className={`h-full ${tone.bar}`}
        style={{ width: `${Math.min(100, counter.percent)}%` }}
      />
    </div>
  </div>
);

export const ResendQuotaPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ daily: "", monthly: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/email-quota`,
        { withCredentials: true }
      );
      setData(res.data);
      setForm({
        daily: String(res.data?.daily?.limit ?? ""),
        monthly: String(res.data?.monthly?.limit ?? ""),
      });
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/api/system-notifications/email-quota`,
        { daily: Number(form.daily), monthly: Number(form.monthly) },
        { withCredentials: true }
      );
      setData(res.data);
      toast.success("Limites de quota enregistrées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Limites non enregistrées");
    } finally {
      setSaving(false);
    }
  };

  // Rien à dire en dessous du seuil : un panneau permanent finit par être ignoré.
  if (!data?.daily || !data?.monthly || data.level === "ok") return null;

  const tone = TONE[data.level] || TONE.warning;

  return (
    <section
      className={`mb-6 rounded-xl border px-4 py-3 ${tone.box}`}
      data-testid="resend-quota-panel"
    >
      <div className="flex items-start gap-3">
        <Gauge size={20} className={`mt-0.5 shrink-0 ${tone.icon}`} />
        <div className={`w-full text-sm ${tone.text}`}>
          <p className="font-semibold" data-testid="resend-quota-message">
            {data.message}
          </p>
          <p className="text-xs opacity-80 mt-0.5">
            Clé{" "}
            {data.key_source === "byok"
              ? "de cette organisation"
              : "partagée Knoria"}{" "}
            · expéditeur <strong>{data.sender_email}</strong>
            {data.quota_reason ? ` · Resend : « ${data.quota_reason} »` : ""}
          </p>

          <Bar
            label={`Aujourd'hui (${data.day})`}
            counter={data.daily}
            tone={tone}
            testid="resend-quota-daily"
          />
          <Bar
            label={`Ce mois (${data.month})`}
            counter={data.monthly}
            tone={tone}
            testid="resend-quota-monthly"
          />

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs">
              Limite / jour
              <input
                type="number"
                min="1"
                value={form.daily}
                onChange={(e) =>
                  setForm((f) => ({ ...f, daily: e.target.value }))
                }
                className="mt-1 block w-24 rounded-lg border border-zinc-300 px-2 py-1 text-sm text-zinc-900"
                data-testid="resend-quota-daily-limit"
              />
            </label>
            <label className="text-xs">
              Limite / mois
              <input
                type="number"
                min="1"
                value={form.monthly}
                onChange={(e) =>
                  setForm((f) => ({ ...f, monthly: e.target.value }))
                }
                className="mt-1 block w-28 rounded-lg border border-zinc-300 px-2 py-1 text-sm text-zinc-900"
                data-testid="resend-quota-monthly-limit"
              />
            </label>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              data-testid="resend-quota-save"
            >
              {saving ? "Enregistrement…" : "Enregistrer les limites"}
            </button>
            <span className="text-xs opacity-70">
              Plan gratuit Resend : {data.default_daily}/jour,{" "}
              {data.default_monthly}/mois
            </span>
          </div>

          <p className="mt-2 text-xs opacity-80">
            Un courriel de rappel part une fois par jour à partir de{" "}
            {data.alert_percent} %, via « Notifications système → Diagnostic
            Forfait AI ».
          </p>
        </div>
      </div>
    </section>
  );
};

export default ResendQuotaPanel;
